import { createFileRoute } from "@tanstack/react-router";
import NavBar from "#/components/navbar/navbar";
import Footer from "#/components/footer/footer";
import TemplateShowcase from "#/components/cv-templates/Showcase";
import Mdx from "#/components/MdxRemote";
import { metadataToHead, type PageMetadata } from "#/lib/seo";

export default function Page() {
  const links = [
    {
      href: "/physician-salary-in-united-states",
      text: "Physician Salary Guide",
    },
    { href: "/physician-cv-examples", text: "Resume CV Examples" },
  ];

  return (
    <>
      <header>
        <NavBar overrideLinks={links} />
      </header>

      <main id="content" className="mx-auto mt-10 max-w-3xl space-y-10">
        <h1 className="px-5 text-center text-5xl">
          CV Examples for Physicians
        </h1>

        <section className="mx-8 space-y-5">
          <p>
            You've worked way too hard to let a poorly formatted CV hold you
            back. Use one of our templates designed for medical doctors and
            stand out from the stack of poorly designed CVs.
          </p>

          <p>
            Avoid being overlooked by keeping the hiring manager engaged while
            they learn why you're the one they've been looking for. This can be
            the difference between landing the dream position and experiencing
            the frustration of an unnecessarily long job search.
          </p>
        </section>

        <section className="mx-8 space-y-5">
          <h2 className="text-xl font-bold">Physician CV Sample Templates</h2>
          <TemplateShowcase templates={templates} />
        </section>

        <section className="mx-8 space-y-5">
          <Mdx source={content} />
        </section>
      </main>
      <Footer fmBio />
    </>
  );
}

const templates = [
  {
    id: 1,
    title: "Modern Physician CV",
    imageUrl: "/physician-cv-resume-example-template.png",
    description:
      "A sleek and modern design suitable for physicians at any career stage.",
  },
  {
    id: 2,
    title: "Elegant Professional Resume",
    imageUrl: "/physician-cv-resume-example-template.png",
    description:
      "An elegant template that combines professionalism with a touch of sophistication.",
  },
  {
    id: 3,
    title: "Minimalist CV Design",
    imageUrl: "/physician-cv-resume-example-template.png",
    description:
      "Perfect for those who prefer a minimalist and clean layout for their CV.",
  },
  {
    id: 4,
    title: "Locum Tenens CV Template",
    imageUrl: "/physician-cv-resume-example-template.png",
    description:
      "A dynamic layout that stands out, ideal for showcasing skills and experience.",
  },
  {
    id: 5,
    title: "Classic Physician CV",
    imageUrl: "/physician-cv-resume-example-template.png",
    description:
      "A classic template that emphasizes clarity and structure, great for traditional roles.",
  },
];

export const metadata: PageMetadata = {
  title: `${templates.length} Physician CV Resume Examples in 2024`,
  description:
    "Stand out from the stack of boring CVs with our templates for physicians. Get the hiring manager's attention and keep'em engaged.",
  openGraph: {
    images: ["https://roamdoc.com/og-image.png"],
  },
  alternates: {
    canonical: `https://www.roamdoc.com/physician-cv-examples`,
  },
};

const content = `
## How to Write an Effective CV for Physicians

### What Makes Medicial Doctor CVs Unique
An physician education and training is distinctive and rigorous. It includes medical school, followed by residency and often fellowship training. Detailing this specialized training journey is crucial in the CV.

Our profession demands a blend of high-level technical skills and exceptional patient care abilities. An effective CV for an physician should not only list qualifications and clinical experiences but also demonstrate these skills. This includes detailing your proficiency in various medical techniques, patient monitoring, and pain management strategies, as well as showcasing your ability to work collaboratively with surgical teams and other healthcare professionals.

Additionally, given the rapid advancements in our field, showcasing your commitment to ongoing education and professional development is crucial. This can be reflected through continuous learning initiatives, participation in workshops, and attendance at relevant conferences.

The ability to handle high-stress situations, make quick decisions, and provide compassionate care during critical moments is central to our role. While these soft skills are challenging to convey on paper, your experiences, particularly those involving emergency or complex cases, can reflect these qualities.

### What to include in your CV

#### Personal Information
Begin with your full name, phone number, email, and LinkedIn profile. Ensure these details are current and professional.

Clearly state your professional title.

#### Education and Training
List your medical school, location, and the dates attended. Highlight any distinctions or honors received. 

Detail your residency and any fellowships, including the institution, department, and the focus of your training. Emphasize any specialized training relevant to the role you're applying for.

#### Licenses and Certifications
Clearly list your board certifications, specifying the certifying body and the year of certification.

Include all states where you hold a current medical license, noting any additional qualifications or endorsements.

Mention any additional certifications you have obtained, such as Advanced Cardiovascular Life Support (ACLS) or Pediatric Advanced Life Support (PALS).

#### Clinical Skills and Techniques
Detailing specific medical techniques and procedures you are proficient in or experience with particular patient demographics (pediatrics, geriatrics), can provide a clearer picture of your clinical capabilities.

#### Interdisciplinary Collaboration
Emphasize experiences where you collaborated with other healthcare professionals. 

#### Leadership and Management Experience
If applicable, mention any leadership roles you’ve held, such as heading a department, leading a committee, or mentoring junior colleagues. This showcases your ability to take on responsibilities beyond clinical duties.

#### Continuing Medical Education (CME)
Continuous learning is crucial in medicine. Including recent CME activities can demonstrate your commitment to staying current in your field.

#### Digital Proficiency
In the modern healthcare environment, familiarity with electronic medical records (EMR) systems and telemedicine can be a significant advantage.

#### Soft Skills
Soft skills like communication, empathy, and problem-solving are crucial. Highlight instances where these skills played a key role in patient care or team dynamics.

#### Customization for Different Job Roles
While not a separate section, it’s important to remember to tailor the CV for different job applications, emphasizing relevant experience and skills for each role.

#### References
While not always included in a CV, having a list of professional references ready can be beneficial, especially if requested.
`;

export const Route = createFileRoute("/(resume-examples)/physician-cv-examples/")({
  component: Page,
  head: () => metadataToHead(metadata),
});
